//Libraries
import React, { memo } from 'react';
import styled from 'styled-components';

import { ReactComponent as ErrorIcon } from 'assets/@efz/icons/error.svg';
import { ReactComponent as InfoIcon } from 'assets/@efz/icons/info.svg';
// icons
import { ReactComponent as SuccessIcon } from 'assets/@efz/icons/success.svg';
import { ReactComponent as WarningIcon } from 'assets/@efz/icons/warning.svg';

import { fonts } from 'constants/styles';
import { themeBase } from 'constants/styles/global';

import { isFieldDefined } from 'services/util/auxiliaryUtils';

const iconMapping = {
    success: SuccessIcon,
    warning: WarningIcon,
    info: InfoIcon,
    error: ErrorIcon,
};

type Severity = 'error' | 'success' | 'warning' | 'info';

interface StyledAlertProps {
    severity: Severity;
}

const StyledAlert = styled.div.attrs({
    className: 'efz-alert',
})<StyledAlertProps>`
    border-radius: 8px;
    font-size: 0.875rem;
    padding: 0.875rem 1rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    --icon-size: 18px;

    .alert-container {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        svg {
            width: var(--icon-size);
            height: var(--icon-size);
        }
    }

    ${(props) => {
        const { severity } = props;

        switch (severity) {
            case 'error':
                return `
                    background-color: ${themeBase.color.efzRed50};
                    border: 1px solid ${themeBase.color.efzRed400};
                    .efz-alert-icon,
                    .efz-alert-icon svg {
                        fill: ${themeBase.color.efzRed400};
                    }
                `;
            case 'success':
                return `
                    background-color: ${themeBase.color.success50};
                    border: 1px solid ${themeBase.color.success600};
                    .efz-alert-icon,
                    .efz-alert-icon svg {
                        fill: ${themeBase.color.success600};
                    }
                `;
            case 'warning':
                return `
                    background-color: ${themeBase.color.warning50};
                    border: 1px solid ${themeBase.color.warning600};
                    .efz-alert-icon,
                    .efz-alert-icon svg {
                        fill: ${themeBase.color.warning600};
                    }
                `;
            case 'info':
            default:
                return `
                    background-color: ${themeBase.color.info50};
                    border: 1px solid var(--blue-500);
                    .efz-alert-icon,
                    .efz-alert-icon svg {
                        fill: ${themeBase.color.info600};
                    }
                `;
        }
    }}
`;

const AlertTitle = styled.div`
    font-size: 0.875rem;
    line-height: var(--icon-size);
    margin: 0;
    font-weight: 500;
    color: var(--steel-950);
    font-weight: 500;
    font-family: ${fonts.base};
`;

const AlertBody = styled.div`
    margin-top: 0.25rem;
    margin-left: calc(var(--icon-size) + 0.5rem);
    font-size: 0.875rem;
    line-height: 1.188rem;
    color: var(--steel-800);
    font-weight: 400;
`;

interface AlertProps {
    severity?: Severity;
    className?: string;
    IntlMessages: React.ReactElement;
    secondaryText?: React.ReactNode;
}

const Alert = ({ severity = 'info', className = '', IntlMessages, secondaryText }: AlertProps) => {
    const IconComponent = iconMapping[severity as Severity] || iconMapping.info;

    return (
        <StyledAlert severity={severity} className={`efz-alert-${severity} ${className}`}>
            <div className="alert-container">
                <IconComponent />
                <AlertTitle className="efz-alert-title">{IntlMessages}</AlertTitle>
            </div>
            {isFieldDefined(secondaryText) && <AlertBody className="efz-alert-body">{secondaryText}</AlertBody>}
        </StyledAlert>
    );
};

export default memo(Alert);
